import React, { useState, useEffect, useMemo } from 'react';
import { AIAgent, ModelType, AIModelsResponse } from '../../types';
import AIModelService from '../../services/api/aiModelService';
import { CreateAgentRequest, UpdateAgentRequest } from '../../services/api/aiAgentService';
import { TaskySelect } from '../tasky';

interface UserAgentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent?: AIAgent;
  onSubmit: (agentData: CreateAgentRequest | UpdateAgentRequest) => Promise<void>;
}

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * font-size は line-height 未指定 or 別指定なので arbitrary value で保持する。 */
const MODAL_BACKDROP =
  'fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[1000]';

const MODAL_CONTENT =
  'w-[90%] max-w-[600px] max-h-[90vh] overflow-y-auto bg-white rounded-tasky-md ' +
  'shadow-[0_10px_40px_rgba(0,0,0,0.3)] relative';

const MODAL_HEADER = 'p-6 border-b border-b-gray-200 flex justify-between items-center'; /* #E5E7EB */

const MODAL_TITLE = 'text-[1.25rem] font-semibold text-gray-800 m-0'; /* #1F2937 */

const CLOSE_BUTTON =
  'bg-transparent border-0 text-[1.5rem] text-gray-500 cursor-pointer w-8 h-8 flex ' +
  'justify-center items-center rounded hover:bg-gray-100 hover:text-gray-800'; /* #6B7280 / #F3F4F6 */

const MODAL_BODY = 'p-6';

const FORM_GROUP = 'mb-6';

const LABEL = 'block text-[0.875rem] font-medium text-gray-700 mb-2'; /* #374151 */

const REQUIRED = 'text-red-500 ml-1'; /* #EF4444 */

const FIELD_BASE =
  'w-full p-2.5 border border-gray-300 rounded-tasky-xs text-[0.875rem] ' +
  'transition-[border-color] duration-200 focus:outline-none focus:border-blue-600 ' +
  'focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)]'; /* #D1D5DB / #2563EB */

const INPUT = FIELD_BASE + ' placeholder:text-gray-400'; /* #9CA3AF */

const TEXT_AREA = FIELD_BASE + ' placeholder:text-gray-400 [font-family:inherit] resize-y min-h-[120px]';

const ERROR_MESSAGE = 'mt-2 text-[0.75rem] text-red-500';

const FORM_ERROR =
  'px-4 py-3 bg-red-100 text-red-700 rounded-tasky-xs mb-4 text-[0.875rem]'; /* #FEE2E2 / #B91C1C */

const HELP_TEXT = 'mt-2 text-[0.75rem] text-gray-500 leading-[1.4]'; /* #6B7280 */

const COLOR_PICKER_CONTAINER = 'mb-6';

const COLORS_GRID = 'grid grid-cols-[repeat(7,1fr)] gap-2 mt-2';

/* 背景色は DEFAULT_COLORS から実行時に決まるので style で渡す（クラス名は合成しない） */
const COLOR_OPTION_BASE =
  'w-10 h-10 rounded-full cursor-pointer border-[3px] transition-all duration-200 ' +
  'hover:scale-110 hover:shadow-[0_2px_8px_rgba(0,0,0,0.15)]';
const COLOR_OPTION_SELECTED = 'border-blue-600'; /* #2563EB */
const COLOR_OPTION_UNSELECTED = 'border-transparent';

const MODAL_FOOTER = 'px-6 py-4 border-t border-t-gray-200 flex justify-end gap-3';

const BUTTON_BASE =
  'px-5 py-2.5 rounded-tasky-xs text-[0.875rem] font-medium cursor-pointer transition-all duration-200';
const BUTTON_PRIMARY =
  'border-0 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed'; /* #2563EB / #1D4ED8 / #93C5FD */
const BUTTON_DEFAULT =
  'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'; /* #D1D5DB / #F9FAFB */

const LOADING_MESSAGE = 'p-4 text-center text-gray-500 text-[0.875rem]';

// Color options for agent cards
const DEFAULT_COLORS = [
  '#FFC107', // Yellow
  '#FF9800', // Orange
  '#F44336', // Red
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#3F51B5', // Indigo
  '#2196F3', // Blue
  '#00BCD4', // Cyan
  '#009688', // Teal
  '#4CAF50', // Green
  '#8BC34A', // Light Green
  '#CDDC39', // Lime
  '#FF5722', // Deep Orange
  '#795548', // Brown
];

// Fallback model options
const FALLBACK_MODEL_OPTIONS = [
  { value: 'chatgpt-4o', label: 'ChatGPT 4o (OpenAI)' },
  { value: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet (Anthropic)' },
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Google)' },
];

const UserAgentFormModal: React.FC<UserAgentFormModalProps> = ({
  isOpen,
  onClose,
  agent,
  onSubmit,
}) => {
  const [modalVisible, setModalVisible] = useState(isOpen);
  const isEditMode = !!agent;

  // Form state
  const [title, setTitle] = useState(agent?.title || '');
  const [description, setDescription] = useState(agent?.description || '');
  const [modelType, setModelType] = useState<ModelType | ''>(agent?.model_type || '');
  const [promptTemplate, setPromptTemplate] = useState(agent?.prompt_template || '');
  const [baseColor, setBaseColor] = useState(agent?.base_color || '#FFC107');

  // AI models state
  const [modelsData, setModelsData] = useState<AIModelsResponse | null>(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setModalVisible(isOpen);

    // Disable body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fetch AI models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        setModelsError(null);
        AIModelService.clearCache();
        const data = await AIModelService.getModelsWithCache(true);
        setModelsData(data);
      } catch (error) {
        console.error('Failed to fetch models:', error);
        setModelsError('モデル一覧の取得に失敗しました');
      } finally {
        setModelsLoading(false);
      }
    };

    if (isOpen) {
      fetchModels();
    }
  }, [isOpen]);

  // Generate model options
  const modelOptions = useMemo(() => {
    if (!modelsData?.success || !modelsData.data?.categories) {
      return FALLBACK_MODEL_OPTIONS;
    }

    const options: Array<{ value: string; label: string }> = [];

    Object.entries(modelsData.data.categories).forEach(([categoryKey, category]) => {
      if (category.models && category.models.length > 0) {
        category.models.forEach(model => {
          options.push({
            value: model.type,
            label: `${model.display_name}`,
          });
        });
      }
    });

    return options.length > 0 ? options : FALLBACK_MODEL_OPTIONS;
  }, [modelsData]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'エージェント名は必須です';
    } else if (title.length > 255) {
      newErrors.title = 'エージェント名は255文字以内で入力してください';
    }

    if (!modelType) {
      newErrors.modelType = 'AIモデルを選択してください';
    }

    if (!promptTemplate.trim()) {
      newErrors.promptTemplate = 'プロンプトは必須です';
    } else if (promptTemplate.length > 5000) {
      newErrors.promptTemplate = 'プロンプトは5000文字以内で入力してください';
    }

    if (description && description.length > 1000) {
      newErrors.description = '説明は1000文字以内で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setFormError(null);

      const agentData = {
        title,
        description: description || undefined,
        model_type: modelType as string,
        prompt_template: promptTemplate,
        base_color: baseColor,
      };

      await onSubmit(agentData);
      onClose();
    } catch (error: any) {
      console.error('Failed to save agent:', error);
      setFormError(error?.message || 'エージェントの保存に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!modalVisible) {
    return null;
  }

  return (
    <div className={MODAL_BACKDROP} onClick={onClose}>
      <div className={MODAL_CONTENT} onClick={(e) => e.stopPropagation()}>
        <div className={MODAL_HEADER}>
          <h2 className={MODAL_TITLE}>
            {isEditMode ? 'エージェントを編集' : '新しいエージェントを作成'}
          </h2>
          <button className={CLOSE_BUTTON} onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className={MODAL_BODY}>
          {formError && <div className={FORM_ERROR}>{formError}</div>}

          <form onSubmit={handleSubmit}>
            <div className={FORM_GROUP}>
              <label className={LABEL} htmlFor="agent-title">
                エージェント名<span className={REQUIRED}>*</span>
              </label>
              <input className={INPUT}
                id="agent-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="例: マーケティングアシスタント"
                disabled={isSubmitting}
              />
              {errors.title && <div className={ERROR_MESSAGE}>{errors.title}</div>}
            </div>

            <div className={FORM_GROUP}>
              <label className={LABEL} htmlFor="agent-description">説明</label>
              <input className={INPUT}
                id="agent-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="例: マーケティング戦略の立案をサポート"
                disabled={isSubmitting}
              />
              {errors.description && <div className={ERROR_MESSAGE}>{errors.description}</div>}
            </div>

            <div className={FORM_GROUP}>
              <label className={LABEL} htmlFor="agent-model-type">
                AIモデル<span className={REQUIRED}>*</span>
              </label>
              {modelsLoading ? (
                <div className={LOADING_MESSAGE}>モデル一覧を読み込み中...</div>
              ) : (
                <>
                  <TaskySelect
                    id="agent-model-type"
                    value={modelType}
                    onChange={(e) => setModelType(e.target.value as ModelType)}
                    disabled={isSubmitting}
                  >
                    <option value="">選択してください</option>
                    {modelOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TaskySelect>
                  {errors.modelType && <div className={ERROR_MESSAGE}>{errors.modelType}</div>}
                </>
              )}
            </div>

            <div className={FORM_GROUP}>
              <label className={LABEL} htmlFor="agent-prompt-template">
                プロンプト<span className={REQUIRED}>*</span>
              </label>
              <textarea className={TEXT_AREA}
                id="agent-prompt-template"
                value={promptTemplate}
                onChange={(e) => setPromptTemplate(e.target.value)}
                placeholder="あなたは優秀なマーケティングアシスタントです。ユーザーの質問に対して、具体的で実践的なアドバイスを提供してください。"
                disabled={isSubmitting}
              />
              <p className={HELP_TEXT}>
                このエージェントの役割や応答スタイルを記述してください。ここに書いた内容がAIの動作を決定します。
              </p>
              {errors.promptTemplate && <div className={ERROR_MESSAGE}>{errors.promptTemplate}</div>}
            </div>

            <div className={COLOR_PICKER_CONTAINER}>
              <label className={LABEL}>アイコンカラー</label>
              <div className={COLORS_GRID}>
                {DEFAULT_COLORS.map((color) => (
                  <div
                    key={color}
                    className={`${COLOR_OPTION_BASE} ${baseColor === color ? COLOR_OPTION_SELECTED : COLOR_OPTION_UNSELECTED}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setBaseColor(color)}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </form>
        </div>

        <div className={MODAL_FOOTER}>
          <button className={`${BUTTON_BASE} ${BUTTON_DEFAULT}`} onClick={onClose} disabled={isSubmitting}>
            キャンセル
          </button>
          <button
            className={`${BUTTON_BASE} ${BUTTON_PRIMARY}`}
            onClick={handleSubmit}
            disabled={isSubmitting || modelsLoading}
          >
            {isSubmitting ? '保存中...' : isEditMode ? '更新する' : '作成する'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserAgentFormModal;
